import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { parsePermissions, getAuthSessionId } from "../utils/authStorage";
import {
  ACTIVITY_LOG_PERMISSION,
  hasActivityLogPermission,
  hasDirectorMessagesPermission,
  hasReferralPermission,
} from "../utils/sosPermissions";

export default function Sidebar() {
  const [sessionId, setSessionId] = useState(getAuthSessionId);

  useEffect(() => {
    const refresh = () => setSessionId(getAuthSessionId());
    window.addEventListener("storage", refresh);
    window.addEventListener("auth-changed", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("auth-changed", refresh);
    };
  }, []);

  const permissions = useMemo(() => parsePermissions(), [sessionId]);
  const role = localStorage.getItem("Role");

  const enquiryEndpoints = [
    "/Enquiries",
    "/Air_Medical_Escort",
    "/Ambulance_Service",
    "/Treatment_Estimate",
    "/SOS_Emergency",
    "/sos/list",
    "/Campaign_Contacts",
    "/Chatbot_Leads",
  ];
  const hasEnquiryAccess = enquiryEndpoints.some((ep) =>
    permissions.includes(ep),
  );
  const generalEnquiryEndpoints = [
    "/General_Enquiries",
    "/Medical_Visa",
    "/Guest_House_Stay",
    "/Forex_Service",
    "/Flight_Service",
    "/Pickup_and_Drop",
    "/Home_Care",
    "/Test_Form",
    "/Contact_Us",
  ];

  const hasGeneralEnquiryAccess = generalEnquiryEndpoints.some((ep) =>
    permissions.includes(ep),
  );
  const menuItems = [
    { path: "/Dashboard", icon: "fa-dashboard", label: "Dashboard" },
    {
      path: "/Enquiries",
      actualPath: "/Admin/Inquiry",
      icon: "fa-comments-o",
      label: "Enquiries",
      condition: hasEnquiryAccess,
    },
    {
      path: "/General_Enquiries",
      actualPath: "/Admin/General_Enquiries",
      icon: "fa-comments-o",
      label: "General Enquiries",
      condition: hasGeneralEnquiryAccess,
    },
    {
      path: "/Manage_Patients",
      actualPath: "/Admin/patients",
      icon: "fa-wheelchair",
      label: "Manage Patients",
    },
    {
      path: "/Patient_Travel",
      actualPath: "/Admin/patient-travel",
      icon: "fa-plane",
      label: "Patient Travel",
      condition:
        role === "Admin" ||
        permissions.includes("/Patient_Travel") ||
        permissions.includes("/Manage_Patients"),
    },
    {
      path: "/Physiotherapy",
      actualPath: "/Admin/physiotherapy",
      icon: "fa-heartbeat",
      label: "Physiotherapy",
      condition:
        role === "Admin" ||
        permissions.includes("/Physiotherapy") ||
        permissions.includes("/Manage_Patients") ||
        permissions.length === 0,
    },
    {
      path: "/Messages",
      actualPath: "/Admin/messages",
      icon: "fa-commenting",
      label: "Messages",
      condition: true,
    },
    {
      path: "/Director_Messages",
      actualPath: "/Admin/director-messages",
      icon: "fa-bullhorn",
      label: "Director Messages",
      condition: true,
    },
    {
      path: "/Manage_Appointments",
      actualPath: "/Admin/Appointments",
      icon: "fa-calendar",
      label: "Manage Appointments",
    },
    {
      path: "/Orders",
      actualPath: "/Admin/orders",
      icon: "fa-shopping-cart",
      label: "Orders",
      condition:
        role === "Admin" ||
        permissions.includes("/Orders") ||
        permissions.includes("/orders") ||
        permissions.length === 0,
    },
    {
      path: "/Manage_Services",
      actualPath: "/Admin/Services",
      icon: "fa-server",
      label: "Manage Services",
    },
    {
      path: "/Manage_Countries",
      actualPath: "/Admin/Countries",
      icon: "fa-globe",
      label: "Manage Countries",
    },
    {
      path: "/Guest_Houses",
      actualPath: "/Admin/guesthouses",
      icon: "fa-building",
      label: "Guest Houses",
      condition:
        role === "Admin" ||
        permissions.includes("/Guest_Houses") ||
        permissions.includes("/Guest_House_Stay") ||
        permissions.includes("/Manage_Guest_Houses"),
    },
    {
      path: "/Guest_House_Services",
      actualPath: "/Admin/guesthouse-services",
      icon: "fa-concierge-bell",
      label: "Guest House Services",
      condition:
        role === "Admin" ||
        permissions.includes("/Guest_House_Services") ||
        permissions.includes("/Guest_Houses") ||
        permissions.includes("/Guest_House_Stay") ||
        permissions.includes("/Manage_Guest_Houses") ||
        permissions.length === 0,
    },
    {
      path: "/Referral",
      actualPath: "/Admin/referrals",
      icon: "fa-handshake-o",
      label: "Referral",
      condition:
        role === "Admin" ||
        hasReferralPermission(permissions, role) ||
        permissions.includes("/Referral") ||
        permissions.includes("/referrals") ||
        permissions.includes("/Manage_Referrals"),
    },
    {
      path: "/Manage_Staffs",
      actualPath: "/Admin/Staff",
      icon: "fa-user-md",
      label: "Manage Staff",
    },
    {
      path: "/Manage_Permissions",
      actualPath: "/Admin/New-Permission",
      icon: "fa-lock",
      label: "Manage Permission",
    },
    {
      path: "/History",
      actualPath: "/Admin/History",
      icon: "fa-history",
      label: "History",
    },
    {
      path: ACTIVITY_LOG_PERMISSION,
      actualPath: "/Admin/activity-logs",
      icon: "fa-list-alt",
      label: "Activity Log",
      condition: hasActivityLogPermission(permissions, role),
    },
    {
      path: "/Reports",
      actualPath: "/Admin/Reports",
      icon: "fa-file-text-o",
      label: "Reports",
    },
    {
      path: "/Payments",
      actualPath: "/Admin/Earnings",
      icon: "fa-line-chart",
      label: "Payments",
    },
    {
      path: "/Payment_Data",
      actualPath: "/Admin/payment-data",
      icon: "fa-credit-card",
      label: "Payment Data",
      condition: role === "Admin" || permissions.includes("/Payment_Data"),
    },
    {
      path: "/Manage_Roles",
      actualPath: "/Admin/roles",
      icon: "fa-line-chart",
      label: "Manage Roles",
    },
  ];

  const handleLinkClick = () => {
    if (window.innerWidth < 1200) {
      const sidebar = document.getElementById('sidebar');
      const sidebarOverlay = document.querySelector('.sidebar-overlay');
      const mainWrapper = document.querySelector('.main-wrapper');
      if (sidebar) sidebar.classList.remove('opened');
      if (sidebarOverlay) sidebarOverlay.classList.remove('opened');
      if (mainWrapper) mainWrapper.classList.remove('slide-nav');
      document.documentElement.classList.remove('menu-opened');
    }
  };

  if (role === "REFERRAL") {
    return (
      <div className="sidebar" id="sidebar">
        <div className="sidebar-inner slimscroll">
          <div id="sidebar-menu" className="sidebar-menu">
            <ul>
              <li>
                <NavLink
                  to="/Dashboard"
                  className={({ isActive }) => (isActive ? "active" : "")}
                  onClick={handleLinkClick}
                >
                  <i className="fa fa-dashboard" />
                  <span>Dashboard</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/Admin/Inquiry"
                  className={({ isActive }) => (isActive ? "active" : "")}
                  onClick={handleLinkClick}
                >
                  <i className="fa fa-comments-o" />
                  <span>Enquiries</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/Admin/General_Enquiries"
                  className={({ isActive }) => (isActive ? "active" : "")}
                  onClick={handleLinkClick}
                >
                  <i className="fa fa-comments-o" />
                  <span>General Enquiries</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/Admin/patients"
                  className={({ isActive }) => (isActive ? "active" : "")}
                  onClick={handleLinkClick}
                >
                  <i className="fa fa-wheelchair" />
                  <span>Manage Patients</span>
                </NavLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar" id="sidebar">
      <div className="sidebar-inner slimscroll">
        <div id="sidebar-menu" className="sidebar-menu">
          <ul>
            {menuItems.map((item) => {
              const hasPermission =
                item.condition !== undefined
                  ? item.condition
                  : permissions.includes(item.path);

              if (hasPermission) {
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.actualPath || item.path}
                      className={({ isActive }) => (isActive ? "active" : "")}
                      onClick={handleLinkClick}
                    >
                      <i className={`fa ${item.icon}`} />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
